import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { GET_OFFLINE_PAYMENT_INVOICE } from "@/Graphql/Queries";
import { generateMockData } from "@/Graphql/MockData";
import client from "@/Graphql/apolloClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { 
  ArrowLeft, 
  FileText, 
  Calendar, 
  CreditCard,
  Clock,
  Eye,
  Download,
  CheckCircle,
  Building2,
  Receipt,
  ChevronLeft,
  ChevronRight,
  AlertCircle
} from "lucide-react";
import logger from '@/utils/logger';
interface PaymentInvoiceViewProps {
  base_path: string;
}

export default function PaymentInvoiceView({ base_path }: PaymentInvoiceViewProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showFullImage, setShowFullImage] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<any>(null);
  const [offlineInvoiceData, setOfflineInvoiceData] = useState<any>(null);

  // Get campaign ID and access token
  const campaignId = searchParams.get('campaign') || '';
  const token = localStorage.getItem("authToken") || '';

  logger.log("Payment Invoice View - Campaign ID:", campaignId);
  logger.log("Payment Invoice View - Auth Token:", token ? "Present" : "Missing");

  // Fetch offline payment invoice data
  useEffect(() => {
    const fetchInvoiceData = async () => {
      if (!campaignId || !token) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        let invoiceData = null;

        if (import.meta.env.VITE_APP_MOCK_DATA === "true") {
          const mockResponse: any = await generateMockData(GET_OFFLINE_PAYMENT_INVOICE);
          invoiceData = mockResponse.data?.offlinePaymentInvoice;
        } else {
          const { data } = await client.query({
            query: GET_OFFLINE_PAYMENT_INVOICE,
            variables: {
              accessToken: token,
              campaignId: campaignId,
              status: 'APPROVED'
            },
            fetchPolicy: 'network-only'
          });
          invoiceData = data?.offlinePaymentInvoice;
        }

        setOfflineInvoiceData(invoiceData);
        setError(null);
      } catch (err) {
        logger.error("Error fetching offline payment invoice:", err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchInvoiceData();
  }, [campaignId, token]);

  logger.log("Payment Invoice View - Query State:", { loading, error, data: offlineInvoiceData });
  const paymentTransaction = offlineInvoiceData?.paymentTransaction;
  const campaign = offlineInvoiceData?.campaign;
  const assets = offlineInvoiceData?.assets || [];

  // Map campaign data from API response
  const campaignData = campaign ? {
    id: campaign.id,
    name: campaign.name,
    code: campaign.dealId || `Campaign ${campaign.id}`,
    duration: `${campaign.startDate?.dateStr || ''} - ${campaign.endDate?.dateStr || ''}`,
    startDate: campaign.startDate?.date || '',
    endDate: campaign.endDate?.date || '',
    campaignNotes: "Invoice slip from payment system",
    inventories: (campaign.inventoriesSummary?.classicInventories || 0) +
                 (campaign.inventoriesSummary?.digitalInventories || 0) +
                 (campaign.inventoriesSummary?.packages || 0),
    status: paymentTransaction?.status || "Invoice Uploaded",
    totalPrice: campaign.priceSummary?.netTotal || 0,
    invoiceSlips: assets.map((asset: any) => asset.filePath),
    invoiceSlipFilename: assets[0]?.fileName || "Invoice_Slip.png",
    uploadedAt: paymentTransaction?.createdDate || new Date().toISOString(),
    subTotal: campaign.priceSummary?.subTotal || 0,
    gst: campaign.priceSummary?.tax?.value || 0,
    taxName: campaign.priceSummary?.tax?.name || 'GST',
    taxPercent: campaign.priceSummary?.tax?.percent || 0,
    currencyCode: campaign.priceSummary?.currency?.code || 'AUD',
    paymentMethod: paymentTransaction?.paymentProvider?.toLowerCase() || 'offline',
    assets: assets
  } : null;

  const formatCurrency = (amount: number) => {
    const currencyCode = campaignData?.currencyCode || 'AUD';
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: currencyCode
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const handleDownload = async () => {
    if (campaignData?.invoiceSlips?.[currentImageIndex]) {
      try {
        const response = await fetch(campaignData.invoiceSlips[currentImageIndex]);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const fileName = campaignData.assets?.[currentImageIndex]?.fileName || 
                        `${campaignData.invoiceSlipFilename.replace(/\.\w+$/, '')}_${currentImageIndex + 1}.png`;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        logger.error('Download failed:', error);
        window.open(campaignData.invoiceSlips[currentImageIndex], '_blank');
      }
    }
  };

  const handlePreviousImage = () => {
    setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : (campaignData?.invoiceSlips?.length || 1) - 1));
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => (prev < (campaignData?.invoiceSlips?.length || 1) - 1 ? prev + 1 : 0));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50">
        <div className="w-full px-4 sm:px-6 lg:px-16 py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Loading Invoice Details...</h1>
            <p className="text-gray-600">Please wait while we fetch your invoice slip information.</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !campaignData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50">
        <div className="w-full px-4 sm:px-6 lg:px-16 py-8">
          <div className="text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Invoice Not Found</h1>
            <p className="text-gray-600 mb-6">
              {error ? `Error: ${error.message}` : "The requested invoice details could not be found."}
            </p>
            <Button 
              onClick={() => navigate(`${base_path}/my-campaigns`)}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Campaigns
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50">
      <div className="w-full px-4 sm:px-6 lg:px-16 py-8">
        <div className="max-w-7xl mx-auto">
          {/* Header with Back Button on Right */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
                Invoice Slip
              </h1>
              <p className="text-base sm:text-lg text-gray-600">
                Invoice slip for offline payment
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate(`${base_path}/my-campaigns`)}
              className="hover:bg-emerald-100"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Campaigns
            </Button>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Left Side - Invoice Slip Preview */}
            <div className="lg:col-span-2">
              {/* Invoice Photos Count - Above Card */}
              {campaignData.invoiceSlips?.length > 1 && (
                <div className="mb-3 text-sm font-medium text-emerald-600">
                  {campaignData.invoiceSlips.length} invoice photos
                </div>
              )}
              
              <Card className="shadow-lg border-0">
                <CardHeader className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white">
                  <CardTitle className="flex items-center justify-between text-lg sm:text-xl">
                    <div className="flex items-center">
                      <Receipt className="mr-2 h-5 w-5" />
                      Invoice Slip Document
                    </div>
                    <Badge className="bg-green-500 text-white">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Uploaded
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="space-y-4">
                    {/* Invoice Info */}
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-emerald-100 rounded">
                          <FileText className="h-5 w-5 text-emerald-600" />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">
                            {campaignData.invoiceSlipFilename}
                          </div>
                          <div className="text-xs text-gray-500 flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            Uploaded {new Date(campaignData.uploadedAt).toLocaleString('en-AU', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                        </div>
                      </div>
                      <Button
                        onClick={handleDownload}
                        variant="outline"
                        size="sm"
                        className="border-emerald-600 text-emerald-600 hover:bg-emerald-50"
                      >
                        <Download className="h-4 w-4 mr-1" />
                        Download
                      </Button>
                    </div>

                    {/* Invoice Preview */}
                    <div className="border-2 border-gray-200 rounded-lg overflow-hidden">
                      <div className="relative group">
                        <img 
                          src={campaignData.invoiceSlips?.[currentImageIndex] || campaignData.invoiceSlips?.[0]} 
                          alt={`Invoice Slip ${currentImageIndex + 1}`}
                          className="w-full max-h-[500px] object-contain cursor-pointer bg-gray-50"
                          onClick={() => setShowFullImage(true)}
                        />
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <Button
                            onClick={() => setShowFullImage(true)}
                            variant="secondary"
                            size="lg"
                          >
                            <Eye className="h-5 w-5 mr-2" />
                            View Full Size
                          </Button>
                        </div>

                        {/* Navigation arrows for multiple images */}
                        {campaignData.invoiceSlips?.length > 1 && (
                          <>
                            <Button
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePreviousImage();
                              }}
                              variant="secondary"
                              size="icon"
                              className="absolute left-2 top-1/2 -translate-y-1/2 opacity-80 hover:opacity-100"
                            >
                              <ChevronLeft className="h-5 w-5" />
                            </Button>
                            <Button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleNextImage();
                              }}
                              variant="secondary"
                              size="icon"
                              className="absolute right-2 top-1/2 -translate-y-1/2 opacity-80 hover:opacity-100"
                            >
                              <ChevronRight className="h-5 w-5" />
                            </Button>
                          </>
                        )}
                        
                        {/* Image counter - Always show if there are images */}
                        {campaignData.invoiceSlips?.length > 0 && (
                          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/70 text-white px-3 py-1 rounded-full text-sm">
                            {currentImageIndex + 1} / {campaignData.invoiceSlips.length}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-start gap-3 p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                      <CheckCircle className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                      <div className="text-sm text-emerald-800">
                        <p className="font-semibold mb-1">Invoice Uploaded</p>
                        <p>This invoice slip has been uploaded and verified. The invoice can contain 1-20 photos based on invoice size or number of inventory involved. The invoice details have been reflected in the system.</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Side - Campaign Summary */}
            <div className="lg:col-span-1">
              <Card className="shadow-lg border-0 sticky top-8">
                <CardHeader className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white">
                  <CardTitle className="flex items-center text-lg">
                    <FileText className="mr-2 h-5 w-5" />
                    Campaign Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 space-y-4">
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Campaign Name</div>
                    <div className="font-semibold text-gray-900">{campaignData.name}</div>
                  </div>

                  <Separator />

                  <div>
                    <div className="text-xs text-gray-500 mb-1">Campaign Code</div>
                    <div className="font-mono text-sm text-gray-900">{campaignData.code}</div>
                  </div>

                  <Separator />

                  <div>
                    <div className="text-xs text-gray-500 mb-1 flex items-center">
                      <Calendar className="h-3 w-3 mr-1" />
                      Campaign Duration
                    </div>
                    <div className="text-sm text-gray-900">{campaignData.duration}</div>
                  </div>

                  <Separator />

                  <div>
                    <div className="text-xs text-gray-500 mb-1 flex items-center">
                      <Building2 className="h-3 w-3 mr-1" />
                      Inventories
                    </div>
                    <div className="text-sm text-gray-900">{campaignData.inventories} Billboard(s)</div>
                  </div>

                  <Separator />

                  <div className="bg-emerald-50 p-3 rounded-lg">
                    <div className="text-xs text-emerald-700 mb-1 flex items-center">
                      <CreditCard className="h-3 w-3 mr-1" />
                      Payment Method
                    </div>
                    <div className="font-semibold text-emerald-800">
                      Offline Payment
                    </div>
                  </div>

                  <Separator />

                  <div className="space-y-2">
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-600">Subtotal</span>
                      <span className="font-semibold text-gray-900">{formatCurrency(campaignData.subTotal)}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-600">{campaignData.taxName} ({campaignData.taxPercent}%)</span>
                      <span className="font-semibold text-gray-900">{formatCurrency(campaignData.gst)}</span>
                    </div>
                    <Separator />
                    <div className="flex justify-between">
                      <span className="font-bold text-gray-900">Total</span>
                      <span className="font-bold text-emerald-600">{formatCurrency(campaignData.totalPrice)}</span>
                    </div>
                  </div>

                  <div className="pt-4">
                    <Button
                      onClick={() => navigate(`${base_path}/payment-invoice?campaign=${campaignData.id}`)}
                      className="w-full bg-emerald-600 hover:bg-emerald-700"
                    >
                      <Receipt className="h-4 w-4 mr-2" />
                      View Payment Invoice
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Full Image Modal */}
      {showFullImage && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setShowFullImage(false)}
        >
          <div className="relative max-w-6xl w-full">
            <Button
              onClick={() => setShowFullImage(false)}
              variant="secondary"
              size="sm"
              className="absolute top-4 right-4 z-10"
            >
              Close
            </Button>
            
            <img 
              src={campaignData.invoiceSlips?.[currentImageIndex] || campaignData.invoiceSlips?.[0]} 
              alt={`Invoice Slip - Full Size ${currentImageIndex + 1}`}
              className="w-full h-auto rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />

            {/* Navigation for multiple images */}
            {campaignData.invoiceSlips?.length > 1 && (
              <>
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePreviousImage();
                  }}
                  variant="secondary"
                  size="icon"
                  className="absolute left-4 top-1/2 -translate-y-1/2"
                >
                  <ChevronLeft className="h-6 w-6" />
                </Button>
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleNextImage();
                  }}
                  variant="secondary"
                  size="icon"
                  className="absolute right-4 top-1/2 -translate-y-1/2"
                >
                  <ChevronRight className="h-6 w-6" />
                </Button>

                {/* Image counter */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-white px-4 py-2 rounded-full text-sm font-medium">
                  {currentImageIndex + 1} / {campaignData.invoiceSlips.length}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
